import type {TFunction} from "i18next";

import type {CitationTableColumnId} from "./citations-table-columns";
import type {CitationTowStatus} from "../types";
import type {TowWorkflowStatus} from "./tow-workflow";

export function tCitationColumnLabel(
  columnId: CitationTableColumnId,
  tCitation: TFunction<"citation">,
  tCommon: TFunction<"common">,
): string {
  switch (columnId) {
    case "date":
      return tCommon("date");
    case "plate":
      return tCitation("table.plate", {defaultValue: "Plate"});
    case "plate_expires_on":
      return tCitation("table.plate_expires_on", {defaultValue: "Plate expires"});
    case "type":
      return tCommon("type");
    case "reported_by":
      return tCommon("reported_by");
    case "details":
      return tCommon("details");
    case "proof_tow":
      return tCitation("table.proof_tow", {defaultValue: "Proof / Tow"});
    default:
      return columnId;
  }
}

export function tCitationNoDetails(tCitation: TFunction<"citation">): string {
  return tCitation("list.no_details", {defaultValue: "No details"});
}

export function tCitationPlateExpiresNotRecorded(tCitation: TFunction<"citation">): string {
  return tCitation("detail.plate_expires_not_recorded", {defaultValue: "Not recorded"});
}

export function tCitationAllTypesTitle(tCitation: TFunction<"citation">): string {
  return tCitation("list.all_types_title", {defaultValue: "All citation types"});
}

export function tCitationProofRequired(tCitation: TFunction<"citation">): string {
  return tCitation("chip.proof_required");
}

export function tCitationProofOnFile(tCitation: TFunction<"citation">): string {
  return tCitation("chip.proof_on_file");
}

export function tCitationProofLabel(tCitation: TFunction<"citation">): string {
  return tCitation("proof.label", {defaultValue: "Proof"});
}

export function tCitationTowBadgeLabel(
  tCitation: TFunction<"citation">,
  status: TowWorkflowStatus | null,
  englishFallback: string,
): string {
  if (!status) {
    return tCitation("tow.label", {defaultValue: englishFallback});
  }

  switch (status) {
    case "cancelled":
      return tCitation("tow.cancelled", {defaultValue: englishFallback});
    case "completed":
      return tCitation("tow.completed", {defaultValue: englishFallback});
    case "pending":
      return tCitation("tow.pending", {defaultValue: englishFallback});
    case "awaiting_request":
      return tCitation("tow.awaiting_request", {defaultValue: englishFallback});
    default:
      return tCitation("tow.label", {defaultValue: englishFallback});
  }
}

export function tCitationTowFilterLabel(
  tCitation: TFunction<"citation">,
  status: CitationTowStatus,
  englishFallback: string,
): string {
  switch (status) {
    case "none":
      return tCitation("tow.filter.none", {defaultValue: englishFallback});
    case "awaiting_request":
      return tCitation("tow.filter.awaiting_request", {defaultValue: englishFallback});
    case "pending":
      return tCitation("tow.filter.pending", {defaultValue: englishFallback});
    case "completed":
      return tCitation("tow.filter.completed", {defaultValue: englishFallback});
    case "cancelled":
      return tCitation("tow.filter.cancelled", {defaultValue: englishFallback});
  }
}

export function tCitationTowStatusFilterLabel(tCitation: TFunction<"citation">): string {
  return tCitation("filters.tow_status", {defaultValue: "Tow Status"});
}

export function tCitationPermissionDeniedList(tCitation: TFunction<"citation">): string {
  return tCitation("list.permission_denied", {
    defaultValue: "You do not have permission to view citations.",
  });
}

export function tCitationEmptyList(
  tCitation: TFunction<"citation">,
  options: {canCreate: boolean; createAbove?: boolean},
): string {
  if (!options.canCreate) {
    return tCitation("empty", {defaultValue: "No citations found"});
  }
  if (options.createAbove) {
    return tCitation("list.empty_with_create_above", {
      defaultValue: "No citations found. Use New citation above to add one.",
    });
  }
  return tCitation("list.empty_with_create", {
    defaultValue: "No citations found. Use New citation to add one.",
  });
}

export function tCitationDeleteConfirmTitle(tCitation: TFunction<"citation">): string {
  return tCitation("confirm.delete_title", {defaultValue: "Delete citation?"});
}

export function tCitationDeleteConfirmDescription(
  tCitation: TFunction<"citation">,
  citationId: number,
): string {
  return tCitation("confirm.delete_description", {
    defaultValue:
      "This will permanently remove citation #{{id}}. This action cannot be undone.",
    id: citationId,
  });
}

export function tCitationDeleting(tCommon: TFunction<"common">): string {
  return tCommon("processing", {defaultValue: "Deleting…"});
}

export function tCitationStoredVehicleTooltip(
  tCitation: TFunction<"citation">,
  storedVehicleId: number,
): string {
  return tCitation("list.stored_vehicle_tooltip", {
    id: storedVehicleId,
  });
}

export function tCitationViewDetailsAria(tCitation: TFunction<"citation">): string {
  return tCitation("list.view_details_aria", {defaultValue: "View citation details"});
}

export function tCitationRowActionDetailTitle(
  tCitation: TFunction<"citation">,
  canOpen: boolean,
): string {
  if (canOpen) {
    return tCitation("actions.view_details", {defaultValue: "View details"});
  }
  return tCitation("actions.view_details_denied", {
    defaultValue: "You do not have permission to view citation details",
  });
}

export function tCitationRowActionProofTitle(
  tCitation: TFunction<"citation">,
  state: "attach" | "disabled" | "denied" | "on_file" | "not_required",
): string {
  switch (state) {
    case "attach":
      return tCitation("actions.attach_proof", {defaultValue: "Attach client proof"});
    case "disabled":
      return tCitation("actions.proof_disabled", {
        defaultValue: "Proof is not enabled for this citation",
      });
    case "denied":
      return tCitation("actions.proof_denied", {
        defaultValue: "You do not have permission to add proof",
      });
    case "on_file":
      return tCitation("chip.proof_on_file");
    case "not_required":
      return tCitation("actions.proof_not_required", {
        defaultValue: "Proof is not required right now",
      });
  }
}

export function tCitationRowActionTowTitle(
  tCitation: TFunction<"citation">,
  state: "manage" | "disabled" | "denied" | "closed" | "unavailable",
): string {
  switch (state) {
    case "manage":
      return tCitation("actions.manage_tow", {defaultValue: "Manage tow"});
    case "disabled":
      return tCitation("actions.tow_disabled", {
        defaultValue: "Tow is not enabled for this citation",
      });
    case "denied":
      return tCitation("actions.tow_denied", {
        defaultValue: "You do not have permission to manage tow",
      });
    case "closed":
      return tCitation("actions.tow_closed", {
        defaultValue: "Tow workflow is already closed",
      });
    case "unavailable":
      return tCitation("actions.tow_unavailable", {
        defaultValue: "Tow action is not available right now",
      });
  }
}

export function tCitationRowActionEditTitle(
  tCitation: TFunction<"citation">,
  canEdit: boolean,
): string {
  if (canEdit) {
    return tCitation("actions.edit", {defaultValue: "Edit citation"});
  }
  return tCitation("actions.edit_denied", {
    defaultValue: "Only admins and senior supervisors can edit citations",
  });
}

export function tCitationRowActionDeleteTitle(
  tCitation: TFunction<"citation">,
  canDelete: boolean,
): string {
  if (canDelete) {
    return tCitation("actions.delete", {defaultValue: "Delete citation"});
  }
  return tCitation("actions.delete_denied", {
    defaultValue: "Only admins and senior supervisors can delete citations",
  });
}

export function tCitationTowStatusPlaceholder(tCitation: TFunction<"citation">): string {
  return tCitation("filters.tow_status_placeholder", {defaultValue: "Select tow statuses"});
}

export function tCitationFiltersHouseholdLabel(tCitation: TFunction<"citation">): string {
  return tCitation("filters.household", {defaultValue: "Household"});
}

export function tCitationFiltersDateRangeLabel(tCommon: TFunction<"common">): string {
  return `${tCommon("from")} - ${tCommon("to")}`;
}

export function tCitationFiltersAnyTowRequired(tCitation: TFunction<"citation">): string {
  return tCitation("filters.any_tow_required", {defaultValue: "Any tow required"});
}

export function tCitationFiltersAnyTowRequiredHelp(tCitation: TFunction<"citation">): string {
  return tCitation("filters.any_tow_required_help", {
    defaultValue: "Citations flagged for tow at issuance, any lifecycle stage.",
  });
}

export function tCitationFiltersTypePlaceholder(tCitation: TFunction<"citation">): string {
  return tCitation("filters.type_placeholder", {defaultValue: "Select citation types"});
}

export function tCitationFiltersTypeEmpty(tCitation: TFunction<"citation">): string {
  return tCitation("filters.type_empty", {defaultValue: "No citation types found."});
}

export function tCitationFiltersDetailsPlaceholder(tCitation: TFunction<"citation">): string {
  return tCitation("filters.details_placeholder", {
    defaultValue: "Substring match on citation details",
  });
}

export function tCitationFiltersUserPlaceholder(tCitation: TFunction<"citation">): string {
  return tCitation("filters.user_placeholder", {defaultValue: "Select user"});
}

export function tCitationFiltersVehicleSearchPlaceholder(
  tCitation: TFunction<"citation">,
  minLength: number,
): string {
  const english = `Search vehicles (min ${minLength} characters)`;
  return tCitation("filters.vehicle_search_placeholder", {
    defaultValue: english,
    min: minLength,
  });
}

export function tCitationFiltersVehicleSearchEmpty(
  tCitation: TFunction<"citation">,
  minLength: number,
): string {
  const english = `Type at least ${minLength} characters to search vehicles`;
  return tCitation("filters.vehicle_search_empty", {
    defaultValue: english,
    min: minLength,
  });
}
